import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, TextField, Chip, AppBar, Toolbar, Menu, MenuItem, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import AddIcon from '@mui/icons-material/Add';
import VotingSystem from '../components/VotingSystem';
import { doc, getDoc, setDoc, addDoc, collection, deleteDoc } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseConfig } from '../firebaseConfig';
import { initializeApp } from 'firebase/app';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const storage = getStorage(app);

// Function to read bucket data from Firebase
const readBucketDataFromFirebase = async (bucketId: string) => {
  try {
    const docRef = doc(db, 'marketingBucketList', bucketId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      console.log(`No such document: ${bucketId}`);
      return null;
    }
  } catch (error) {
    console.error(`Error reading bucket data from Firebase:`, error);
    return null;
  }
};

// Function to save data to Firebase
const saveDataToFirebase = async (bucketId: string, data: any) => {
  try {
    const docRef = doc(db, 'marketingBucketList', bucketId);
    await setDoc(docRef, { ...data, updatedAt: new Date() }, { merge: true });
    console.log(`Data saved to bucket ${bucketId} successfully`);
  } catch (error) {
    console.error(`Error saving to bucket ${bucketId}:`, error);
  }
};

// Editable Text Component
const EditableText: React.FC<{
  value: string;
  onSave: (value: string) => void;
  variant?: 'h3' | 'h4' | 'h5' | 'h6' | 'body1' | 'body2';
  sx?: any;
  placeholder?: string;
  multiline?: boolean;
}> = ({ value, onSave, variant = 'body1', sx = {}, placeholder = '', multiline = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        variant="outlined"
        placeholder={placeholder}
        fullWidth
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        maxRows={multiline ? 6 : undefined}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h3' ? '1.5rem' : variant === 'h4' ? '1.5rem' : variant === 'h5' ? '1.25rem' : variant === 'h6' ? '1.25rem' : '1rem',
            fontWeight: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? 'bold' : 'normal',
            minHeight: 'auto',
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          '& .MuiInputBase-input': {
            padding: variant === 'h3' || variant === 'h4' || variant === 'h5' || variant === 'h6' ? '8px 12px' : '4px 8px',
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        display: 'inline-block',
        minWidth: variant === 'h3' ? '80px' : '60px',
        minHeight: '1.2em',
        border: '1px dashed transparent',
        borderRadius: 1,
        padding: '2px 4px',
        transition: (theme) => theme.transitions.create(['background-color', 'border-color']),
        '&:hover': {
          borderColor: 'divider',
          backgroundColor: 'action.hover',
        },
        ...sx,
      }}
    >
      {value || placeholder}
    </Box>
  );
};

// Editable Number Component
const EditableNumber: React.FC<{
  value: number;
  onSave: (value: number) => void;
  formatCurrency?: boolean;
  variant?: 'h3' | 'h6' | 'body1' | 'body2';
  sx?: any;
}> = ({ value, onSave, formatCurrency = false, variant = 'body1', sx = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  useEffect(() => {
    setEditValue(value.toString());
  }, [value]);

  const handleSave = () => {
    const newValue = parseFloat(editValue);
    if (!isNaN(newValue) && newValue !== value) {
      onSave(newValue);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(value.toString());
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <TextField
        type="number"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        autoFocus
        size="small"
        sx={{
          width: '120px',
          '& .MuiOutlinedInput-root': {
            fontSize: variant === 'h6' ? '1.25rem' : '1rem',
          },
          ...sx
        }}
      />
    );
  }

  return (
    <Box
      component="span"
      onClick={() => setIsEditing(true)}
      sx={{
        typography: variant,
        cursor: 'pointer',
        minWidth: '50px',
        display: 'inline-block',
        ...sx
      }}
    >
      {formatCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()}
    </Box>
  );
};

// EditableChip Component for Status/Scope/Return
const EditableChip: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onSave: (value: string) => void;
  getColor: (value: string) => "default" | "error" | "warning" | "info" | "success" | "primary" | "secondary";
}> = ({ value, options, onSave, getColor }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    onSave(newValue);
    handleClose();
  };

  const currentOption = options.find(opt => opt.value === value);

  return (
    <>
      <Chip
        label={currentOption ? currentOption.label : value}
        onClick={handleClick}
        color={getColor(value)}
        size="small"
        sx={{ cursor: 'pointer', textTransform: 'capitalize' }}
      />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {options.map((option) => (
          <MenuItem key={option.value} onClick={() => handleSelect(option.value)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

// File handling functions
const handleFileUpload = async (files: File[], bucketId: string, bucketData: any, setBucketData: any, saveBucketData: any) => {
  const uploadedFiles = [];
  
  for (const file of files) {
    try {
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}_${file.name}`;
      const storageRef = ref(storage, `marketing/${bucketId}/${fileName}`);
      
      // Upload file to Firebase Storage
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Add file info to the list
      uploadedFiles.push({
        name: file.name,
        size: file.size,
        url: downloadURL,
        uploadedAt: new Date(),
        storagePath: `marketing/${bucketId}/${fileName}`
      });
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  }
  
  // Update bucket data with new files
  const updatedFiles = [...(bucketData.files || []), ...uploadedFiles];
  const updatedData = { ...bucketData, files: updatedFiles };
  setBucketData(updatedData);
  await saveBucketData('files', updatedFiles);
};

const handleFileDelete = async (index: number, bucketId: string, bucketData: any, setBucketData: any, saveBucketData: any) => {
  try {
    const fileToDelete = bucketData.files[index];
    
    // Delete from Firebase Storage
    if (fileToDelete.storagePath) {
      const storageRef = ref(storage, fileToDelete.storagePath);
      await deleteObject(storageRef);
    }
    
    // Remove from bucket data
    const updatedFiles = bucketData.files.filter((_: any, i: number) => i !== index);
    const updatedData = { ...bucketData, files: updatedFiles };
    setBucketData(updatedData);
    await saveBucketData('files', updatedFiles);
  } catch (error) {
    console.error('Error deleting file:', error);
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// URL handling functions
const handleAddUrl = async (title: string, url: string, bucketId: string, bucketData: any, setBucketData: any, saveBucketData: any) => {
  if (!url.trim()) return;
  
  const newUrl = {
    title: title.trim() || 'Untitled',
    url: url.trim(),
    addedAt: new Date()
  };
  
  const updatedUrls = [...(bucketData.urls || []), newUrl];
  const updatedData = { ...bucketData, urls: updatedUrls };
  setBucketData(updatedData);
  await saveBucketData('urls', updatedUrls);
};

const handleDeleteUrl = async (index: number, bucketId: string, bucketData: any, setBucketData: any, saveBucketData: any) => {
  const updatedUrls = bucketData.urls.filter((_: any, i: number) => i !== index);
  const updatedData = { ...bucketData, urls: updatedUrls };
  setBucketData(updatedData);
  await saveBucketData('urls', updatedUrls);
};

// Function to move bucket to marketing plan
const moveToMarketingPlan = async (bucketData: any, bucketId: string) => {
  try {
    // Create the plan document
    const planData = {
      name: bucketData.name,
      description: bucketData.description,
      expectedBudget: bucketData.expectedBudget || 0,
      status: bucketData.status || 'open',
      expectedScope: bucketData.expectedScope || 'mid',
      expectedReturn: bucketData.expectedReturn || 'mid',
      files: bucketData.files || [],
      urls: bucketData.urls || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Add to marketing collection
    const planRef = await addDoc(collection(db, 'marketingPlan'), planData);

    // Delete from bucket list
    const bucketRef = doc(db, 'marketingBucketList', bucketId);
    await deleteDoc(bucketRef);

    console.log(`Marketing bucket ${bucketId} moved to plan ${planRef.id} successfully`);
    return planRef.id;
  } catch (error) {
    console.error('Error moving bucket to marketing plan:', error);
    return null;
  }
};

interface MarketingBucketListProps {
  bucketId: string;
  bucketName: string;
  onBackToBuckets: () => void;
}

const MarketingBucketList: React.FC<MarketingBucketListProps> = ({ bucketId, bucketName, onBackToBuckets }) => {
  console.log('MarketingBucketList render - bucketId:', bucketId, 'bucketName:', bucketName);
  
  const [bucketData, setBucketData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [newUrl, setNewUrl] = useState('');
  const [newUrlTitle, setNewUrlTitle] = useState('');
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  // Load bucket data from Firebase
  const loadBucketData = async (bucketId: string) => {
    setLoading(true);
    try {
      const data = await readBucketDataFromFirebase(bucketId);
      setBucketData(data);
    } catch (error) {
      console.error('Error loading bucket data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load bucket data when bucket is selected
  useEffect(() => {
    if (bucketId) {
      loadBucketData(bucketId);
    }
  }, [bucketId]);

  const handleMoveToPlan = async () => {
    if (!bucketData) return;
    
    setLoading(true);
    const planId = await moveToMarketingPlan(bucketData, bucketId);
    if (planId) {
      setMoveDialogOpen(false);
      onBackToBuckets(); // Go back to bucket list after successful move
    }
    setLoading(false);
  };

  // Save functions for different data types
  const saveBucketData = async (field: string, value: any) => {
    if (!bucketData || !bucketId) return;
    const updatedData = { ...bucketData, [field]: value };
    setBucketData(updatedData);
    await saveDataToFirebase(bucketId, updatedData);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'in discussion': return 'warning';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const getScopeColor = (scope: string) => {
    switch (scope) {
      case 'high': return 'error';
      case 'mid': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getReturnColor = (returnValue: string) => {
    switch (returnValue) {
      case 'high': return 'success';
      case 'mid': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="bg-gray-100 text-gray-800 min-h-screen flex items-center justify-center">
        <Typography variant="h6">Loading bucket data...</Typography>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 text-gray-800 min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">        
        <AppBar position="sticky" sx={{ top: 16, borderRadius: 2, mb: 4, zIndex: 50 }}>
          <Toolbar sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button 
                variant="text" 
                onClick={onBackToBuckets}
                sx={{ color: 'white' }}
              >
                ← Back to Bucket List
              </Button>
              <Typography variant="h6" sx={{ color: 'white', ml: 2 }}>
                {bucketName}
              </Typography>
              <VotingSystem 
                collectionName="marketingBucketList" 
                documentId={bucketId} 
                size="small"
                showCounts={true}
                showTooltips={true}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DriveFileMoveIcon />}
                onClick={() => setMoveDialogOpen(true)}
                sx={{ color: 'white' }}
              >
                Move to Plan
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        
        <main>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h4" component="h2" gutterBottom>
                    <EditableText 
                      value={bucketData?.name || 'Untitled Idea'} 
                      onSave={(newValue) => saveBucketData('name', newValue)} 
                      variant="h4"
                    />
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <EditableText 
                      value={bucketData?.description || 'No description available'} 
                      onSave={(newValue) => saveBucketData('description', newValue)} 
                      variant="body1"
                      multiline={true}
                      sx={{ color: 'text.secondary' }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Expected Budget
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body1" color="text.secondary">
                      USD
                    </Typography>
                    <EditableNumber
                      value={bucketData?.expectedBudget || 0}
                      onSave={(newValue) => saveBucketData('expectedBudget', newValue)}
                      formatCurrency={true}
                      variant="h6"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Status
                  </Typography>
                  <EditableChip
                    value={bucketData?.status || 'open'}
                    options={[
                      { value: 'open', label: 'Open' },
                      { value: 'in discussion', label: 'In Discussion' },
                      { value: 'accepted', label: 'Accepted' },
                      { value: 'declined', label: 'Declined' },
                    ]}
                    onSave={(newValue) => saveBucketData('status', newValue)}
                    getColor={getStatusColor}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Expected Scope
                  </Typography>
                  <EditableChip
                    value={bucketData?.expectedScope || 'mid'}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'mid', label: 'Mid' },
                      { value: 'high', label: 'High' },
                    ]}
                    onSave={(newValue) => saveBucketData('expectedScope', newValue)}
                    getColor={getScopeColor}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Expected Return
                  </Typography>
                  <EditableChip
                    value={bucketData?.expectedReturn || 'mid'}
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'mid', label: 'Mid' },
                      { value: 'high', label: 'High' },
                    ]}
                    onSave={(newValue) => saveBucketData('expectedReturn', newValue)}
                    getColor={getReturnColor}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    URL Links
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add relevant URLs and links for this marketing idea.
                  </Typography>
                  
                  {/* URL Input */}
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <TextField
                        placeholder="Title (e.g., Company Website)"
                        variant="outlined"
                        size="small"
                        value={newUrlTitle}
                        onChange={(e) => setNewUrlTitle(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (newUrl.trim()) {
                              handleAddUrl(newUrlTitle, newUrl, bucketId, bucketData, setBucketData, saveBucketData);
                              setNewUrl('');
                              setNewUrlTitle('');
                            }
                          }
                        }}
                        sx={{ minWidth: '200px', flexGrow: 1 }}
                      />
                      <TextField
                        placeholder="Enter URL (e.g., https://example.com)"
                        variant="outlined"
                        size="small"
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (newUrl.trim()) {
                              handleAddUrl(newUrlTitle, newUrl, bucketId, bucketData, setBucketData, saveBucketData);
                              setNewUrl('');
                              setNewUrlTitle('');
                            }
                          }
                        }}
                        sx={{ minWidth: '300px', flexGrow: 2 }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          if (newUrl.trim()) {
                            handleAddUrl(newUrlTitle, newUrl, bucketId, bucketData, setBucketData, saveBucketData);
                            setNewUrl('');
                            setNewUrlTitle('');
                          }
                        }}
                        sx={{ minWidth: 'auto', px: 2 }}
                      >
                        Add
                      </Button>
                    </Box>
                  </Box>

                  {/* URL List */}
                  {bucketData?.urls && bucketData.urls.length > 0 && (
                    <List>
                      {bucketData.urls.map((urlItem: any, index: number) => (
                        <ListItem key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                          <ListItemText
                            primary={
                              <a 
                                href={urlItem.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ color: '#1976d2', textDecoration: 'none' }}
                              >
                                {urlItem.title}
                              </a>
                            }
                            secondary={`${urlItem.url} • Added: ${new Date(urlItem.addedAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteUrl(index, bucketId, bucketData, setBucketData, saveBucketData)}
                              sx={{ color: 'error.main' }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h5" component="h3" gutterBottom>
                    Files
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Upload PDFs and other relevant files for this marketing idea.
                  </Typography>
                  
                  {/* File Upload */}
                  <Box sx={{ mb: 3 }}>
                    <input
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
                      style={{ display: 'none' }}
                      id="file-upload"
                      multiple
                      type="file"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files) {
                          handleFileUpload(Array.from(files), bucketId, bucketData, setBucketData, saveBucketData);
                        }
                      }}
                    />
                    <label htmlFor="file-upload">
                      <Button variant="outlined" component="span">
                        Upload Files
                      </Button>
                    </label>
                  </Box>

                  {/* File List */}
                  {bucketData?.files && bucketData.files.length > 0 && (
                    <List>
                      {bucketData.files.map((file: any, index: number) => (
                        <ListItem key={index} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                          <ListItemText
                            primary={file.name}
                            secondary={`Size: ${formatFileSize(file.size)} • Uploaded: ${new Date(file.uploadedAt).toLocaleDateString()}`}
                          />
                          <ListItemSecondaryAction>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => window.open(file.url, '_blank')}
                              >
                                View
                              </Button>
                              <IconButton
                                size="small"
                                onClick={() => handleFileDelete(index, bucketId, bucketData, setBucketData, saveBucketData)}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </main>
      </div>

      {/* Move to Plan Confirmation Dialog */}
      <Dialog
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
        aria-labelledby="move-dialog-title"
      >
        <DialogTitle id="move-dialog-title">
          Move to Marketing Plan
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to move "{bucketData?.name}" from the bucket list to a marketing plan?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setMoveDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleMoveToPlan}
            color="success"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Moving...' : 'Move to Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MarketingBucketList; 